/**
 * Formatting helpers for market-aware currency + area unit display.
 *
 * Storage stays canonical: price is a raw number in the market currency,
 * `square_meters` is always metric square meters. These helpers convert
 * for display only.
 */

import { getMarketConfig } from '@/lib/markets';

const SQM_TO_SQFT = 10.7639;

export function formatPriceForMarket(
  priceLike: string | number | null | undefined,
  market: string | null | undefined,
  opts: { withSymbolOnly?: boolean; currency?: string | null } = {},
): string {
  const num = typeof priceLike === 'number' ? priceLike : parseFloat(String(priceLike ?? ''));
  if (!isFinite(num)) return '';
  const explicit = opts.currency && /^[A-Za-z]{3}$/.test(opts.currency) ? opts.currency.toUpperCase() : null;
  const currency = explicit || getMarketConfig(market).currency;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `${currency} ${Math.round(num).toLocaleString()}`;
  }
}


export function formatAreaForMarket(
  squareMeters: string | number | null | undefined,
  market: string | null | undefined,
): string {
  const num = typeof squareMeters === 'number' ? squareMeters : parseFloat(String(squareMeters ?? ''));
  if (!isFinite(num) || num <= 0) return '';
  const unit = getMarketConfig(market).areaUnit;
  if (unit === 'sqft') {
    return `${Math.round(num * SQM_TO_SQFT).toLocaleString()} sqft`;
  }
  return `${Math.round(num).toLocaleString()} m²`;
}

export function getMarketCurrency(market: string | null | undefined): string {
  return getMarketConfig(market).currency;
}

export function getMarketAreaUnit(market: string | null | undefined): 'sqm' | 'sqft' {
  return getMarketConfig(market).areaUnit;
}